import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
} from '@angular/forms';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { AbstractMethodComponent } from '../abstract-method.component';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { getInvalidFieldListFromFormGroup } from '../../../../misc/get-invalid-field-list-from-form-group';
import { OrderFormValidationResult } from '@orderform/widgets';

declare let unzer: any;

const UNZER_EXTERNAL_SCRIPT = 'https://static.unzer.com/v1/unzer.js';
const UNZER_EXTERNAL_CSS = 'https://static.unzer.com/v1/unzer.css';

@Component({
  selector: 'orderform-payment-method-unzer',
  templateUrl: './unzer.component.html',
  styleUrls: ['./unzer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UnzerComponent),
      multi: true,
    },
  ],
})
export class UnzerComponent
  extends AbstractMethodComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  private _waitForInit: Promise<void>;

  @Input() form: UntypedFormControl;
  @Input() config: { public_key: string };
  @Output() readonly stateChange = new EventEmitter<'VALID' | 'INVALID'>();
  private _unzerCardInstance: any;
  private _subscriptionFormValidation =
    this._orderformFascade.formValidation.subscribe(
      () => this._onFormValidation(),
      this._element
    );
  private _subscriptionBeforeSubmit =
    this._orderformFascade.beforeSubmit.subscribe(() => this._onBeforeSubmit());
  public creditCardType$ = new Subject<string>();
  public paymentFieldSuccessState$ = {
    number: new BehaviorSubject<boolean>(false),
    expiry: new BehaviorSubject<boolean>(false),
    cvc: new BehaviorSubject<boolean>(false),
  };
  public unzerErrorByField$ = {
    number: new BehaviorSubject<string>(''),
    expiry: new BehaviorSubject<string>(''),
    cvc: new BehaviorSubject<string>(''),
  };
  private _unzer_crd_nmbr: any;
  private _paymentProviderStatusSubscription: Subscription;

  constructor(private _orderformFascade: OrderformFascade, injector: Injector) {
    super(injector);
    this._waitForInit = this._init();

    this._paymentProviderStatusSubscription = this._orderformFascade.order$
      .pipe(
        map((e) => {
          return e.order.payment_provider.status?.message;
        }),
        distinctUntilChanged()
      )
      .subscribe(() => {
        // Reset
        this._unzer_crd_nmbr = null;
        this._paymentDataChanged.next({
          valid: true,
        });
      });
  }

  async _init() {
    return await this._loadExternalResources(
      [UNZER_EXTERNAL_SCRIPT],
      [UNZER_EXTERNAL_CSS]
    );
  }

  ngOnInit(): void {}

  async ngAfterViewInit() {
    await this._waitForInit;
    this._initUnzerForm();
  }

  private _initUnzerForm() {
    const ccContainerIdCC = 'cc_number-' + this.uniqueInstanceId;
    const ccContainerIdExp = 'cc_expiry-' + this.uniqueInstanceId;
    const ccContainerIdCvc = 'cc_cvc-' + this.uniqueInstanceId;
    this._moveDomElementsOnDestroy.push(
      ccContainerIdCC,
      ccContainerIdExp,
      ccContainerIdCvc
    );
    const unzerInstance = new unzer(this.config.public_key);
    this._unzerCardInstance = unzerInstance.Card();
    this._unzerCardInstance.create('number', {
      containerId: ccContainerIdCC,
      //onlyIframe: true,
      fontSize: '16px',
      fontColor: 'rgba(4, 37, 62, 0.8)',
      fontWeight: 'bold',
      autofillCC: true,
    });
    this._unzerCardInstance.create('expiry', {
      containerId: ccContainerIdExp,
      //onlyIframe: true,
      fontSize: '16px',
      fontColor: 'rgba(4, 37, 62, 0.8)',
      fontWeight: 'bold',
      autofillCC: true,
    });
    this._unzerCardInstance.create('cvc', {
      containerId: ccContainerIdCvc,
      //onlyIframe: true,
      fontSize: '16px',
      fontColor: 'rgba(4, 37, 62, 0.8)',
      fontWeight: 'bold',
      autofillCC: true,
    });

    this._unzerCardInstance.addEventListener('change', (e) => {
      if (this.paymentFieldSuccessState$[e.type]) {
        this.paymentFieldSuccessState$[e.type].next(e.success);
        if (e.success) {
          this.unzerErrorByField$[e.type].next('');
        } else {
          this.unzerErrorByField$[e.type].next(e.error);
        }
      }
      if (e.cardType) {
        this.creditCardType$.next(e.cardType.type);
      }
      this._paymentDataChanged.next({
        valid: this._isValid(),
      });
    });
  }

  private _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }
    if (this._unzer_crd_nmbr) {
      this._paymentDataChanged.next({
        valid: true,
        unzer_crd_nmbr: this._unzer_crd_nmbr,
      });
      return Promise.resolve({
        valid: true,
      });
    }

    return this._unzerCardInstance
      .createResource()
      .then((data) => {
        this._unzer_crd_nmbr = data.id;
        this._paymentDataChanged.next({
          unzer_crd_nmbr: data.id,
          valid: true,
        });

        return {
          valid: true,
        };
      })
      .catch((error) => {
        if (this.unzerErrorByField$[error.type]) {
          this.unzerErrorByField$[error.type].next(error.message);
          this.paymentFieldSuccessState$[error.type].next(error.success);
          Object.keys(this.unzerErrorByField$)
            .filter((field) => field !== error.type)
            .forEach((field) => this.unzerErrorByField$[field].next(''));
        } else {
          this._toaster.error(error.message);
        }
        this._paymentDataChanged.next({
          valid: false,
        });
        Object.keys(this.unzerErrorByField$)
          .filter((field) => !!this.unzerErrorByField$[field].value)
          .forEach((field) => {
            this._monitoring.trackValidationError(
              field + ':' + this.unzerErrorByField$[field].value,
              'frontend',
              'ds-orderform-payment-unzer'
            );
          });
        return {
          valid: false,
        };
      });
  }

  private _moveDomElementsOnDestroy = [];

  ngOnDestroy() {
    super.ngOnDestroy();
    this._paymentProviderStatusSubscription.unsubscribe();
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
    this._moveDomElementsOnDestroy
      .map((id) => document.getElementById(id))
      .filter((id) => !!id)
      .forEach((element) => {
        element.style.display = 'none';
        document.body.appendChild(element);
      });
    /**
     * @TODO: implement unzer card destroy!
     */
  }

  private _isValid(): boolean {
    return (
      this.paymentFieldSuccessState$.number.getValue() &&
      this.paymentFieldSuccessState$.expiry.getValue() &&
      this.paymentFieldSuccessState$.cvc.getValue()
    );
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive() || this._isValid()) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-unzer',
        invalidFieldList: [],
      });
    }
    const invalidFieldList = Object.keys(this.paymentFieldSuccessState$)
      .filter((field) => !this.paymentFieldSuccessState$[field].value)
      .map((field) => ({
        id: field,
        errors: [this.unzerErrorByField$[field].value],
      }));
    return Promise.resolve({
      valid: false,
      component: 'ds-orderform-payment-unzer',
      invalidFieldList,
    });
  }
}
